/**
 * @fileoverview Section Metadata Contract (Contract-First Design)
 * @module types/contracts
 *
 * Defines section structure for dynamic percentage-based scoring.
 * Single source of truth for question counts per section.
 */

import { z } from 'zod';

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Section Metadata Schema
 *
 * Defines a test section with its question range and derived properties
 * for dynamic percentage calculation.
 */
export const SectionMetadataSchema = z.object({
	/** Section identifier (matches SectionId type) */
	id: z.string(),
	/** Human-readable title */
	title: z.string(),
	/** Question range [start, end] (1-indexed, inclusive) */
	questionRange: z.tuple([z.number().int().positive(), z.number().int().positive()]),
	/** Wizard phase (1 = modalities, 2 = assessments) */
	phase: z.union([z.literal(1), z.literal(2)]),
});
export type SectionMetadata = z.infer<typeof SectionMetadataSchema>;

/**
 * Section Metadata Array Schema
 */
export const SectionMetadataArraySchema = z.array(SectionMetadataSchema);
export type SectionMetadataArray = z.infer<typeof SectionMetadataArraySchema>;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate question count from section metadata
 *
 * @param section - Section metadata with questionRange
 * @returns Number of questions in this section
 */
export function getQuestionCount(section: SectionMetadata): number {
	const [start, end] = section.questionRange;
	return end - start + 1;
}

/**
 * Calculate maximum raw score for a section
 * Max raw score = questionCount × 5 (each question scored 1-5)
 *
 * @param section - Section metadata
 * @returns Maximum possible raw score
 */
export function getMaxRawScore(section: SectionMetadata): number {
	return getQuestionCount(section) * 5;
}

/**
 * Convert raw score to percentage
 *
 * @param rawScore - Sum of answers for the section
 * @param section - Section metadata for max calculation
 * @returns Percentage (0-100), rounded to nearest integer
 */
export function toPercentage(rawScore: number, section: SectionMetadata): number {
	const maxRaw = getMaxRawScore(section);
	if (maxRaw === 0) return 0;
	return Math.round((rawScore / maxRaw) * 100);
}

/**
 * Convert raw score to percentage using explicit max
 *
 * @param rawScore - Sum of answers
 * @param maxRawScore - Maximum possible raw score
 * @returns Percentage (0-100), rounded to nearest integer
 */
export function rawToPercentage(rawScore: number, maxRawScore: number): number {
	if (maxRawScore === 0) return 0;
	return Math.round((rawScore / maxRawScore) * 100);
}

// ============================================================================
// SECTION REGISTRY (Static Definition)
// ============================================================================

/**
 * Complete section registry matching learningTest.json
 *
 * This is the canonical source of truth for section structure.
 * Frontend and backend should import from here for consistency.
 */
export const SECTION_REGISTRY: readonly SectionMetadata[] = [
	// Phase 1: Learning Modalities (40 questions total)
	{ id: 'visual', title: 'Visuelle Lernpräferenzen', questionRange: [1, 10], phase: 1 },
	{ id: 'auditory', title: 'Auditive Lernpräferenzen', questionRange: [11, 20], phase: 1 },
	{ id: 'reading', title: 'Lese-/Schreib-Lernpräferenzen', questionRange: [21, 30], phase: 1 },
	{ id: 'kinesthetic', title: 'Kinästhetische Lernpräferenzen', questionRange: [31, 40], phase: 1 },
	// Phase 2: Assessments (50 questions total)
	{ id: 'integrated', title: 'Integrierte Lernpräferenzen', questionRange: [41, 50], phase: 2 },
	{ id: 'giftedness', title: 'Hochbegabung', questionRange: [51, 60], phase: 2 },
	{ id: 'auditoryProcessing', title: 'Auditive Wahrnehmung', questionRange: [61, 70], phase: 2 },
	{ id: 'highSensitivity', title: 'Hochsensibilität', questionRange: [71, 80], phase: 2 },
	{ id: 'adhd', title: 'Neuro Diversität', questionRange: [81, 90], phase: 2 },
] as const;

/**
 * Lookup section by ID
 */
export function getSectionById(sectionId: string): SectionMetadata | undefined {
	return SECTION_REGISTRY.find((s) => s.id === sectionId);
}

/**
 * Get sections by phase
 */
export function getSectionsByPhase(phase: 1 | 2): SectionMetadata[] {
	return SECTION_REGISTRY.filter((s) => s.phase === phase);
}
